<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useInformationRequests } from "../../Composables/useInformationRequests.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import _ from 'lodash'
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"
import FormSelectField from "../../Components/FormSelectField.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const storageKey = "Shiftech_InformationRequestList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: informationRequests,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
} = useDataManager({
    baseUrl: '/api/information-requests',
    baseSearchUrl: '/api/search/information-requests',
    defaultSortingField: 'updated_at',
    defaultSortingFieldsOrder: {
        id: 'asc',
        updated_at: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: '#ID', field: 'id', sortable: true, slot: 'id', class: 'col-1' },
    { label: 'Cr\u00e9\u00e9 par', slot: 'user', class: 'col-1' },
    { label: 'V\u00e9hicule', slot: 'vehicle', class: 'col-2' },
    { label: 'Immatriculation', slot: 'license_plate', class: 'col-2' },
    { label: 'Statut', slot: 'status', class: 'col-1' },
    { label: 'Assign\u00e9 \u00e0', slot: 'assigned_to', class: 'col-1' },
    { label: 'Derni\u00e8re MAJ', field: 'updated_at', sortable: true, slot: 'updated_at', class: 'col-1' },
    { label: 'Notifications', slot: 'notifications', class: 'col-1 text-center' },
    { label: 'D\u00e9tails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

const errors = ref(null)
const filters = reactive({
    user: null,
    status: null,
})
const filtersApplied = ref(true)
const isInitialized = ref(false)

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        filtersApplied.value = savedSettings.filtersApplied ?? true

        if (savedSettings.filters) {
            Object.assign(filters, savedSettings.filters)
        }
    }

    loadData(url.value)
})

onMounted(() => {
    isInitialized.value = true
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded, filtersApplied, filters],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded, newFiltersApplied]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            filtersApplied: newFiltersApplied,
            filters: { ...filters },
        })
    },
    { deep: true }
)

watch(
    () => [filters.user, filters.status],
    () => {
        if (isInitialized.value) {
            filtersApplied.value = false
        }
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

function applyFilter() {
    let newUrl = new URL(url.value, window.location.origin)

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== '') {
            newUrl.searchParams.set(key, value)
        } else {
            newUrl.searchParams.delete(key)
        }
    })

    filtersApplied.value = true
    loadData(newUrl.href)
}

const idToDelete = ref(null)

function showModal(modalId, id) {
    idToDelete.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    deleteInformationRequest,
} = useInformationRequests({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header border border-dashed border-top-0 border-start-0 border-end-0">
                    <div class="d-flex align-items-center">
                        <h5 class="card-title mb-0 flex-grow-1">Toutes les demandes d'informations</h5>
                        <router-link :to="{ name:'information-requests.create' }" class="btn btn-secondary" v-if="authUser.can && authUser.can.create_client_request">
                            <i class="ri-add-fill me-1 align-bottom hide-on-mobile"></i> Ajouter
                        </router-link>
                    </div>
                </div>

                <!-- Filters -->
                <div class="card-body">
                    <div class="row g-3">
                        <div class="col-xxl-5 col-sm-12">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchInformationRequests">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <form-select-field
                            name="filters.status"
                            label="Statut"
                            :label-hidden="true"
                            @update:field="filters.status = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.status"
                            bs-class="col-xxl-3 col-sm-4"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les statuts"
                            :liste-options="[
                                { text: 'Statuts ouverts (A traiter, En cours)', value: 'all_open' },
                                { text: 'Statuts ferm\u00e9s', value: 'all_closed' },
                                { text: 'Tous les statuts', value: null },
                                { text: '---', value: null },
                                { text: 'A traiter', value: 'pending' },
                                { text: 'En cours', value: 'in_progress' },
                                { text: 'Ferm\u00e9', value: 'closed' },
                            ]"
                            margin="mb-0"
                        />
                        <form-select-field
                            name="filters.user"
                            label="Utilisateur"
                            :label-hidden="true"
                            @update:field="filters.user = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.user"
                            bs-class="col-xxl-3 col-sm-4"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Toutes les demandes"
                            :liste-options="[
                                { text: 'Mes demandes', value: 'my_tickets' },
                                { text: 'Demandes qui me sont assign\u00e9s', value: 'assigned_to_me' },
                                { text: 'Demandes non assign\u00e9s', value: 'unassigned' },
                                { text: 'Toutes les demandes', value: null },
                            ]"
                            margin="mb-0"
                        />
                        <div class="col-xxl-1 col-sm-4">
                            <button type="button" class="btn w-100" @click.prevent="applyFilter" :class="filtersApplied ? 'btn-dark' : 'btn-warning'">
                                <i class="ri-equalizer-fill me-1 align-bottom"></i>
                                Filtrer
                            </button>
                        </div>
                    </div>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="informationRequests"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- User -->
                        <template #user="{ item }">
                            <div class="d-flex align-items-center">
                                <div class="flex-shrink-0">
                                    <img :src="'/format-image/'+item.data.user.data.profile_picture+'?w=32&h=32&fm=webp&fit=crop'" alt="" class="avatar-xs rounded-circle">
                                </div>
                                <div class="flex-grow-1 ms-2 name">{{ item.data.user.data.last_name }} {{ item.data.user.data.first_name }}</div>
                            </div>
                        </template>

                        <!-- Vehicle -->
                        <template #vehicle="{ item }">
                            {{ item.data.vehicle_brand_name }} - {{ item.data.vehicle_model_name }}
                        </template>

                        <!-- License plate -->
                        <template #license_plate="{ item }">
                            {{ item.data.license_plate }}
                        </template>

                        <!-- Status -->
                        <template #status="{ item }">
                            <span :class="'badge fs-11 bg-'+item.data.status_badge_color+'-subtle text-'+item.data.status_badge_color">
                                {{ item.data.status_label_fr }}
                            </span>
                        </template>

                        <!-- Assigned to -->
                        <template #assigned_to="{ item }">
                            <div class="d-flex align-items-center" v-if="item.data.assigned_to !== null">
                                <div class="flex-shrink-0">
                                    <img :src="'/format-image/'+item.data.assigned_to.data.profile_picture+'?w=32&h=32&fm=webp&fit=crop'" alt="" class="avatar-xs rounded-circle">
                                </div>
                                <div class="flex-grow-1 ms-2 name">{{ item.data.assigned_to.data.last_name }} {{ item.data.assigned_to.data.first_name }}</div>
                            </div>
                            <span v-else>--</span>
                        </template>

                        <!-- Updated at -->
                        <template #updated_at="{ item }">
                            {{ item.data.updated_at_fr }}
                        </template>

                        <!-- Notifications -->
                        <template #notifications="{ item }">
                            <div v-if="authUser.id === item.data.user_id">
                                <span v-if="item.data.notifications_user > 0" class="badge rounded-pill bg-danger fs-11">{{ item.data.notifications_user }}</span>
                            </div>
                            <div v-else-if="authUser.id === item.data.assigned_to_id">
                                <span v-if="item.data.notifications_assigned > 0" class="badge rounded-pill bg-danger fs-11">{{ item.data.notifications_assigned }}</span>
                            </div>
                            <div v-else-if="item.data.assigned_to_id === null && (authUser.role === 'admin' || authUser.role === 'back_office')">
                                <span v-if="item.data.notifications_bo > 0" class="badge rounded-pill bg-danger fs-11">{{ item.data.notifications_bo }}</span>
                            </div>
                            <div v-else>
                                <span v-if="item.data.notifications_assigned > 0" class="badge rounded-pill bg-danger fs-11">{{ item.data.notifications_assigned }}</span>
                            </div>
                        </template>

                        <!-- Details -->
                        <template #details="{ item }">
                            <router-link :to="{ name: 'information-requests.show', params: { id: item.data.id } }" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">
                                Acc\u00e9der
                            </router-link>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'information-requests.edit', params: { id: item.data.id } }" class="text-info" title="Modifier la demande" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer la demande" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer la demande d'information ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteInformationRequest(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous \u00eates sur le point de supprimer une demande d'informations. Confirmer la suppression ?</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
